import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Plus, Loader2, AlertCircle, UserPlus } from 'lucide-react'

const PLATFORM_ROLES = ['super_admin', 'staff', 'support_agent', 'billing_manager'] as const
type PlatformRole = typeof PLATFORM_ROLES[number]

const ROLE_ACCESS: Record<PlatformRole, string[]> = {
  super_admin:     ['Overview', 'Organisations', 'All Users', 'Platform Roles', 'Billing', 'Support Inbox', 'CONORBIT Social', 'Audit Log', 'Platform Settings'],
  staff:           ['Overview', 'Organisations', 'Support Inbox', 'CONORBIT Social'],
  support_agent:   ['Support Inbox (assigned only)', 'Session Status'],
  billing_manager: ['Billing'],
}

const ROLE_COLORS: Record<PlatformRole, string> = {
  super_admin:     'bg-amber-100 text-amber-800',
  staff:           'bg-blue-100 text-blue-800',
  support_agent:   'bg-violet-100 text-violet-800',
  billing_manager: 'bg-emerald-100 text-emerald-800',
}

const ROLE_DISPLAY: Record<PlatformRole, string> = {
  super_admin:     'Platform Administrator',
  staff:           'CONORBIT Staff',
  support_agent:   'Support Agent',
  billing_manager: 'Billing Manager',
}

const INVITE_DEFAULT = { first_name: '', last_name: '', email: '', platform_role: 'staff' as PlatformRole }

export function PlatformRolesView() {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [editing,     setEditing]     = useState<Record<string, PlatformRole>>({})
  const [saving,      setSaving]      = useState<string | null>(null)
  const [showInvite,  setShowInvite]  = useState(false)
  const [inviteForm,  setInviteForm]  = useState(INVITE_DEFAULT)

  const { data: admins } = useQuery({
    queryKey: ['app-admins-roles'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('app_admins' as any)
        .select('id, first_name, last_name, email, platform_role')
        .order('first_name') as any)
      return (data ?? []) as any[]
    }
  })

  const superAdminCount = (admins ?? []).filter(
    (a: any) => a.platform_role === 'super_admin'
  ).length

  const isOnlySuperAdmin = (admin: any) =>
    admin.platform_role === 'super_admin' && superAdminCount === 1

  const saveRole = async (adminId: string) => {
    const newRole = editing[adminId]
    if (!newRole) return
    setSaving(adminId)
    try {
      await (supabase
        .from('app_admins' as any)
        .update({ platform_role: newRole })
        .eq('id', adminId) as any)
      toast({ title: 'Role updated.' })
      qc.invalidateQueries({ queryKey: ['app-admins-roles'] })
      setEditing(e => { const n = { ...e }; delete n[adminId]; return n })
    } catch {
      toast({ title: 'Failed to update role.', variant: 'destructive' })
    } finally {
      setSaving(null)
    }
  }

  const [inviteResult, setInviteResult] = useState<{ email: string; temp_password: string } | null>(null)

  const inviteMember = useMutation({
    mutationFn: async () => {
      const { first_name, last_name, email, platform_role } = inviteForm
      if (!first_name.trim() || !last_name.trim() || !email.trim()) {
        throw new Error('First name, last name, and email are required.')
      }
      const { data: { session } } = await supabase.auth.getSession()
      const result = await supabase.functions.invoke('create-admin-user', {
        body: { first_name: first_name.trim(), last_name: last_name.trim(), email: email.trim().toLowerCase(), platform_role },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      })
      if (result.error) throw new Error(result.error.message)
      const body = result.data as any
      if (body?.error) throw new Error(body.error)
      return body
    },
    onSuccess: (data) => {
      setInviteResult({ email: inviteForm.email, temp_password: data.temp_password })
      setInviteForm(INVITE_DEFAULT)
      qc.invalidateQueries({ queryKey: ['app-admins-roles'] })
    },
    onError: (e: any) => toast({ title: 'Invite failed', description: e.message, variant: 'destructive' }),
  })

  const removeAdmin = useMutation({
    mutationFn: async (adminId: string) => {
      if (!window.confirm('Remove this team member from the platform? They will lose admin access immediately.')) {
        throw new Error('cancelled')
      }
      const { error } = await (supabase.from('app_admins' as any).delete().eq('id', adminId) as any)
      if (error) throw error
    },
    onSuccess: () => {
      toast({ title: 'Team member removed.' })
      qc.invalidateQueries({ queryKey: ['app-admins-roles'] })
    },
    onError: (e: any) => { if (e.message !== 'cancelled') toast({ title: 'Error', description: e.message, variant: 'destructive' }) },
  })

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Platform Roles</h1>
          <p className="text-justify text-muted-foreground text-sm mt-1">
            Manage roles and access for CONORBIT staff members.
          </p>
        </div>
        <Button
          onClick={() => setShowInvite(v => !v)}
          style={{ backgroundColor: '#006B3C' }}
          className="text-white hover:opacity-90 shrink-0 flex items-center gap-1.5"
        >
          <UserPlus className="w-4 h-4" />
          Add Staff Member
        </Button>
      </div>

      {/* Invite success */}
      {inviteResult && (
        <div className="border border-[#006B3C]/30 bg-[#E8F5EE] rounded-xl p-4 space-y-2">
          <p className="text-justify text-[13px] font-semibold text-[#006B3C]">✓ Staff member created</p>
          <p className="text-justify text-[12.5px] text-[#0D2240]">
            <strong>{inviteResult.email}</strong> can now log in at <span className="font-mono">/admin/login</span>.
          </p>
          <div className="flex items-center gap-2 bg-white border border-[#DDD8CE] rounded-lg px-3 py-2">
            <span className="text-[11.5px] text-muted-foreground">Temporary password:</span>
            <span className="font-mono text-[13px] font-medium text-[#0D2240] flex-1">{inviteResult.temp_password}</span>
            <button
              onClick={() => navigator.clipboard.writeText(inviteResult.temp_password).catch(() => {})}
              className="text-[11px] text-[#006B3C] hover:underline"
            >
              Copy
            </button>
          </div>
          <p className="text-justify text-[11px] text-muted-foreground">Share this password securely. They will be required to change it on first login.</p>
          <button onClick={() => setInviteResult(null)} className="text-[11.5px] text-[#006B3C] hover:underline">Dismiss</button>
        </div>
      )}

      {/* Invite form */}
      {showInvite && (
        <div className="border border-[#006B3C]/30 bg-[#E8F5EE]/60 rounded-xl p-4 space-y-4">
          <div className="flex items-center gap-2">
            <Plus size={14} className="text-[#006B3C]" />
            <span className="text-[13px] font-semibold text-[#006B3C]">Add New Staff Member</span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground">First Name *</label>
              <input
                value={inviteForm.first_name}
                onChange={e => setInviteForm(f => ({ ...f, first_name: e.target.value }))}
                placeholder="e.g. Kofi"
                className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Last Name *</label>
              <input
                value={inviteForm.last_name}
                onChange={e => setInviteForm(f => ({ ...f, last_name: e.target.value }))}
                placeholder="e.g. Mensah"
                className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-muted-foreground">Email Address *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={e => setInviteForm(f => ({ ...f, email: e.target.value }))}
                placeholder="staff@conorbit.com"
                className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground">Platform Role</label>
              <select
                value={inviteForm.platform_role}
                onChange={e => setInviteForm(f => ({ ...f, platform_role: e.target.value as PlatformRole }))}
                className="mt-1 w-full px-3 py-2 border border-[#DDD8CE] rounded-lg text-[13px] focus:outline-none focus:border-[#006B3C] bg-white"
              >
                {PLATFORM_ROLES.map(r => (
                  <option key={r} value={r}>{ROLE_DISPLAY[r]}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertCircle size={13} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-justify text-[11.5px] text-amber-700">
              This creates a new admin login. The staff member will receive an invite email with their login credentials and must change their password on first sign-in.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setShowInvite(false); setInviteForm(INVITE_DEFAULT) }}
              className="flex-1 py-2 border border-[#DDD8CE] rounded-lg text-[12.5px] text-muted-foreground hover:bg-white"
            >
              Cancel
            </button>
            <button
              onClick={() => inviteMember.mutate()}
              disabled={inviteMember.isPending || !inviteForm.first_name || !inviteForm.last_name || !inviteForm.email}
              className="flex-1 py-2 bg-[#006B3C] text-white rounded-lg text-[12.5px] font-medium hover:bg-[#005A32] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {inviteMember.isPending && <Loader2 size={13} className="animate-spin" />}
              Create & Invite
            </button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left bg-[#F7F5F0]">
                <th className="px-4 py-3 font-medium text-muted-foreground">Staff Member</th>
                <th className="px-4 py-3 font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-3 font-medium text-muted-foreground">Current Role</th>
                <th className="px-4 py-3 font-medium text-muted-foreground w-48">Change Role</th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody>
              {(admins ?? []).map((admin: any) => {
                const locked = isOnlySuperAdmin(admin)
                return (
                  <tr key={admin.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-3 font-medium">
                      {admin.last_name} {admin.first_name}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{admin.email}</td>
                    <td className="px-4 py-3">
                      <Badge className={ROLE_COLORS[admin.platform_role as PlatformRole] ?? 'bg-muted text-muted-foreground'}>
                        {ROLE_DISPLAY[admin.platform_role as PlatformRole] ?? admin.platform_role}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      {locked ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-bold bg-[#C8A84B]/20 text-[#C8A84B] px-2 py-0.5 rounded-full">
                            Platform Administrator
                          </span>
                          <span
                            className="text-[10px] text-muted-foreground cursor-default"
                            title="Cannot change role of the last Platform Administrator"
                          >
                            🔒
                          </span>
                        </div>
                      ) : (
                        <Select
                          value={editing[admin.id] ?? admin.platform_role}
                          onValueChange={v => setEditing(e => ({ ...e, [admin.id]: v as PlatformRole }))}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PLATFORM_ROLES.map(r => (
                              <SelectItem key={r} value={r}>{ROLE_DISPLAY[r]}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {!locked && editing[admin.id] && editing[admin.id] !== admin.platform_role && (
                          <Button
                            size="sm"
                            onClick={() => saveRole(admin.id)}
                            disabled={saving === admin.id}
                            className="h-7 text-xs"
                          >
                            {saving === admin.id ? 'Saving…' : 'Save'}
                          </Button>
                        )}
                        {!locked && (
                          <button
                            onClick={() => removeAdmin.mutate(admin.id)}
                            disabled={removeAdmin.isPending}
                            className="px-2 py-1 border border-red-200 text-red-500 rounded text-[10.5px] hover:bg-red-50"
                          >
                            Remove
                          </button>
                        )}
                        {locked && (
                          <span className="text-[10px] text-muted-foreground">
                            Transfer role first
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
              {(admins ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No staff members yet. Use "Add Staff Member" to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Role access reference */}
      <div>
        <h2 className="text-base font-semibold mb-4">Role Access Reference</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PLATFORM_ROLES.map(role => (
            <Card key={role}>
              <CardHeader className="pb-2 pt-4">
                <div className="flex items-center gap-2">
                  <Badge className={ROLE_COLORS[role]}>{ROLE_DISPLAY[role]}</Badge>
                </div>
              </CardHeader>
              <CardContent className="pb-4">
                <ul className="space-y-1">
                  {ROLE_ACCESS[role].map(item => (
                    <li key={item} className="text-sm text-muted-foreground flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-muted-foreground shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
